import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { DashboardLayout } from "../components/DashboardLayout";
import assessmentsBg from "../assets/assessments-bg.png";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import FullScreenLoader from "../components/FullScreenLoader";
import { useLanguage } from "../contexts/LanguageContext";

const API_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/assessments";
const SUBSCRIPTION_CHECK_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/user/subscriptionCheck";
const CREATE_SESSION_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/create-subscription-session";

const AssessmentsNewV1 = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("active");
  const [activeList, setActiveList] = useState([]);
  const [aiWorkflowCompletedList, setAiWorkflowCompletedList] = useState([]);
  const [aiPoliciesCompletedList, setAiPoliciesCompletedList] = useState([]);
  const [completedList, setCompletedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("Core");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionCheckLoading, setSubscriptionCheckLoading] =
    useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    company: "",
  });
  const [newAssessment, setNewAssessment] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    stateProv: "",
    country: "CA",
    vertical: "Vertical 1",
    keyContact: "",
    contactTitle: "",
    phone: "",
    email: "",
    position: "Owner",
  });

  const getStatusLabel = useCallback(
    (status) => {
      switch (status) {
        case "completed":
          return t("status.completed");
        case "ai-workflow-completed":
          return t("status.aiWorkflowCompleted");
        case "ai-policies-completed":
          return t("status.aiPoliciesCompleted");
        default:
          return t("status.active");
      }
    },
    [t],
  );

  const authToken = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.authToken || null;
    } catch {
      return null;
    }
  }, []);

  const registerData = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const userId = useMemo(() => {
    if (!registerData) return null;
    return (
      registerData?.userId ||
      registerData?._id ||
      registerData?.user?._id ||
      null
    );
  }, [registerData]);

  const userEmail = useMemo(
    () =>
      registerData?.email ||
      registerData?.user?.email ||
      registerData?.data?.email ||
      "",
    [registerData],
  );

  const userFullName = useMemo(
    () =>
      registerData?.fullName ||
      registerData?.user?.fullName ||
      registerData?.data?.fullName ||
      "",
    [registerData],
  );

  const userPhone = useMemo(
    () =>
      registerData?.phone ||
      registerData?.user?.phone ||
      registerData?.data?.phone ||
      "",
    [registerData],
  );

  const AES_SECRET = useMemo(
    () =>
      (typeof import.meta !== "undefined" &&
        import.meta.env?.VITE_JWT_SECRET) ||
      process.env.REACT_APP_JWT_SECRET ||
      "",
    [],
  );

  const fetchAssessments = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      if (!authToken) {
        setError(t("Not authorized. Please log in."));
        setActiveList([]);
        setCompletedList([]);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json().catch(() => ({}));
      if (data?.success === false) {
        navigate("/");
        return;
      }
      if (!res.ok) {
        throw new Error(data?.message || `Request failed (${res.status})`);
      }

      const rawList = (() => {
        if (
          data &&
          Array.isArray(data.active) &&
          Array.isArray(data.completed)
        ) {
          return [...data.active, ...data.completed];
        }
        if (Array.isArray(data)) {
          return data;
        }
        return [];
      })();

      const toCard = (a) => {
        const interviewees = Array.isArray(a.interviewees)
          ? a.interviewees
          : [];
        const aiWorkflowCompleted = a.aiAssessmentCompleted === true;
        const aiPoliciesCompleted = a.aiPolicyAssessmentCompleted === true;
        let status = "active";

        if (aiWorkflowCompleted && aiPoliciesCompleted) {
          status = "completed";
        } else if (aiWorkflowCompleted) {
          status = "ai-workflow-completed";
        } else if (aiPoliciesCompleted) {
          status = "ai-policies-completed";
        }

        return {
          id:
            a._id ||
            a.id ||
            a.assessmentId ||
            a.companyName ||
            Math.random().toString(),
          assessmentId: a.assessmentId || a._id || a.id,
          company: a.companyName || a.title || t("Untitled"),
          contact: a.keyContact || a.contact || "",
          interviewees,
          progress: calcProgress(interviewees),
          status,
        };
      };

      const activeCards = [];
      const aiWorkflowCompletedCards = [];
      const aiPoliciesCompletedCards = [];
      const completedCards = [];

      rawList.forEach((assessment) => {
        const card = toCard(assessment);
        switch (card.status) {
          case "ai-workflow-completed":
            aiWorkflowCompletedCards.push(card);
            break;
          case "ai-policies-completed":
            aiPoliciesCompletedCards.push(card);
            break;
          case "completed":
            completedCards.push(card);
            break;
          default:
            activeCards.push(card);
        }
      });

      setActiveList(activeCards);
      setAiWorkflowCompletedList(aiWorkflowCompletedCards);
      setAiPoliciesCompletedList(aiPoliciesCompletedCards);
      setCompletedList(completedCards);
    } catch (e) {
      setError(e.message || t("Failed to fetch assessments."));
    } finally {
      setLoading(false);
    }
  }, [authToken, navigate, t]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const handleNewAssessmentClick = async () => {
    setSubscriptionError("");
    if (!authToken) {
      setError(t("Not authorized. Please log in."));
      return;
    }

    try {
      setSubscriptionCheckLoading(true);
      const body = userId ? { userId } : {};
      const response = await axios.post(SUBSCRIPTION_CHECK_URL, body, {
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
      });
      const data = response?.data || {};
      if (data?.subscribed) {
        setDialogOpen(true);
        return;
      }
      setSubscriptionDialogOpen(true);
    } catch (err) {
      console.error("subscription check error", err?.response || err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        t("assessments.unableToValidateSubscription");
      if (
        err?.response?.status === 401 ||
        String(message).toLowerCase().includes("unauthorized")
      ) {
        navigate("/");
        return;
      }
      setSubscriptionError(message);
      setSubscriptionDialogOpen(true);
    } finally {
      setSubscriptionCheckLoading(false);
    }
  };

  const handleStartCheckoutForPlan = async () => {
    setSubscriptionError("");
    if (!userEmail || !userFullName) {
      setSubscriptionError(t("assessments.missingAccountInformation"));
      return;
    }

    try {
      setSubscriptionLoading(true);
      const encryptedPassword =
        registerData?.password && AES_SECRET
          ? CryptoJS.AES.encrypt(registerData.password, AES_SECRET).toString()
          : undefined;

      const payload = {
        fullName: userFullName.trim(),
        phone: userPhone.trim(),
        planOption: selectedPlan,
        email: userEmail.trim(),
        currency: "USD",
        ...(encryptedPassword ? { password: encryptedPassword } : {}),
      };

      localStorage.setItem("bv.pendingSignup", JSON.stringify(payload));

      const sessionResp = await axios.post(CREATE_SESSION_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });
      const sessionData = sessionResp?.data || {};
      if (sessionData?.url) {
        setSubscriptionDialogOpen(false);
        setSubscriptionLoading(false);
        window.location.href = sessionData.url;
        return;
      }

      setSubscriptionError(t("assessments.unableToStartCheckout"));
      localStorage.removeItem("bv.pendingSignup");
    } catch (err) {
      console.error("create session error", err?.response || err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        t("assessments.failedToCreateCheckoutSession");
      setSubscriptionError(message);
      localStorage.removeItem("bv.pendingSignup");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCreate = useCallback(async () => {
    if (!authToken) {
      setError(t("Not authorized. Please log in."));
      return;
    }
    if (!newAssessment.companyName.trim()) {
      setError(t("assessments.companyNameRequired"));
      return;
    }

    const sanitizedName = newAssessment.companyName
      .replace(/[^a-zA-Z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const payload = {
      companyName: sanitizedName?.toUpperCase()?.trim(),
      address1: newAssessment.address1.trim(),
      address2: newAssessment.address2.trim(),
      city: newAssessment.city.trim(),
      state: newAssessment.stateProv,
      country: newAssessment.country,
      vertical: newAssessment.vertical,
      keyContact: newAssessment.keyContact.trim(),
      phone: newAssessment.phone.trim(),
      email: newAssessment.email.trim(),
      position: newAssessment.position,
    };

    try {
      setDialogOpen(false);
      setLoading(true);
      const res = await fetch(API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success === false) {
        navigate("/");
        return;
      }
      if (!res.ok) {
        throw new Error(data?.message || t("assessments.failedToSaveAssessment"));
      }
      await fetchAssessments();
      setNewAssessment({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        stateProv: "",
        country: "CA",
        vertical: "Vertical 1",
        keyContact: "",
        contactTitle: "",
        phone: "",
        email: "",
        position: "Owner",
      });
      setDialogOpen(false);
    } catch (e) {
      setError(e.message || t("assessments.failedToSaveAssessment"));
    } finally {
      setLoading(false);
    }
  }, [authToken, fetchAssessments, navigate, newAssessment, t]);

  const handleDelete = useCallback(
    async (assessmentId) => {
      if (!authToken) {
        setError(t("assessments.notAuthorized"));
        return;
      }
      if (!assessmentId) {
        setError(t("assessments.missingAssessmentId"));
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(API_URL, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({ assessmentId }),
        });
        await res.json().catch(() => ({}));
        await fetchAssessments();
      } catch (e) {
        setError(e.message || t("assessments.failedToDeleteAssessment"));
      } finally {
        setLoading(false);
      }
    },
    [authToken, fetchAssessments, t],
  );

  const displayedList = useMemo(() => {
    switch (activeTab) {
      case "ai-workflow":
        return aiWorkflowCompletedList;
      case "ai-policies":
        return aiPoliciesCompletedList;
      case "completed":
        return completedList;
      default:
        return activeList;
    }
  }, [
    activeTab,
    activeList,
    aiWorkflowCompletedList,
    aiPoliciesCompletedList,
    completedList,
  ]);

  return (
    <DashboardLayout backgroundImage={assessmentsBg}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold font-display tracking-tight text-foreground">
              {t("Assessments")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t("assessments.manageAllCompanyAssessments")}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button
              className="gap-1"
              onClick={handleNewAssessmentClick}
              disabled={subscriptionCheckLoading}
            >
              {subscriptionCheckLoading
                ? t("dashboard.checking")
                : t("Create Assessment")}
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">
                  {t("dashboard.createNewAssessment")}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="companyName">{t("form.companyName")}</Label>
                  <Input
                    id="companyName"
                    value={newAssessment.companyName}
                    onChange={(e) =>
                      setNewAssessment((prev) => ({
                        ...prev,
                        companyName: e.target.value,
                      }))
                    }
                    maxLength={100}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="address1">{t("form.address1")}</Label>
                    <Input
                      id="address1"
                      value={newAssessment.address1}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          address1: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address2">{t("form.address2")}</Label>
                    <Input
                      id="address2"
                      value={newAssessment.address2}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          address2: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">{t("form.city")}</Label>
                    <Input
                      id="city"
                      value={newAssessment.city}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stateProv">{t("form.stateProv")}</Label>
                    <Input
                      id="stateProv"
                      value={newAssessment.stateProv}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          stateProv: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">{t("form.country")}</Label>
                    <Input
                      id="country"
                      value={newAssessment.country}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          country: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vertical">{t("form.vertical")}</Label>
                    <Input
                      id="vertical"
                      value={newAssessment.vertical}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          vertical: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="keyContact">{t("form.keyContact")}</Label>
                    <Input
                      id="keyContact"
                      value={newAssessment.keyContact}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          keyContact: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="position">{t("form.position")}</Label>
                    <Input
                      id="position"
                      value={newAssessment.position}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          position: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("form.contactEmail")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newAssessment.email}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      maxLength={255}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("form.contactPhone")}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={newAssessment.phone}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      maxLength={20}
                    />
                  </div>
                </div>
              </div>
              <Button
                onClick={handleCreate}
                disabled={
                  !newAssessment.companyName.trim() ||
                  !newAssessment.email.trim()
                }
                className="w-full"
              >
                {t("Create Assessment")}
              </Button>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={activeTab === "active" ? "default" : "outline"}
            onClick={() => setActiveTab("active")}
            className="flex-1"
          >
            {t("Active Assessments")} ({activeList.length})
          </Button>
          <Button
            variant={activeTab === "ai-workflow" ? "default" : "outline"}
            onClick={() => setActiveTab("ai-workflow")}
            className="flex-1"
          >
            {t("AI Workflow Completed")} ({aiWorkflowCompletedList.length})
          </Button>
          <Button
            variant={activeTab === "ai-policies" ? "default" : "outline"}
            onClick={() => setActiveTab("ai-policies")}
            className="flex-1"
          >
            {t("AI Policies Completed")} ({aiPoliciesCompletedList.length})
          </Button>
          <Button
            variant={activeTab === "completed" ? "default" : "outline"}
            onClick={() => setActiveTab("completed")}
            className="flex-1"
          >
            {t("Completed Assessments")} ({completedList.length})
          </Button>
        </div>

        {/* Assessment Cards Grid */}
        {loading ? (
          <div className="flex justify-center py-10">
            <FullScreenLoader />
          </div>
        ) : displayedList.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            {t("No assessments yet")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedList.map((assessment) => (
              <Card
                key={assessment.id}
                className="shadow-[var(--card-shadow)] hover:shadow-[var(--card-shadow-hover)] transition-shadow h-full"
              >
                <Link
                  to={`/assessment/${assessment.assessmentId}`}
                  className="block"
                >
                  <CardContent className="pt-5 pb-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold font-display truncate">
                          {assessment.company}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {assessment.contact}
                        </p>
                      </div>
                      <Badge
                        className={
                          assessment.status === "active"
                            ? "bg-green-600 text-white border-green-600 shrink-0 text-[11px]"
                            : "bg-blue-600 text-white border-blue-600 shrink-0 text-[11px]"
                        }
                      >
                        {getStatusLabel(assessment.status)}
                      </Badge>
                    </div>
                    <Progress
                      value={assessment.progress}
                      className="h-2 mt-3"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">
                        {assessment.progress}% {t("dashboard.complete")}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {assessment.interviewees.length} {t("Interviewees")}
                      </span>
                    </div>

                    <div className="flex justify-end pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1 h-7 text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          setDeleteDialog({
                            open: true,
                            id: assessment.assessmentId || assessment.id,
                            company: assessment.company,
                          });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t("Delete")}
                      </Button>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-muted-foreground py-4 border-t">
          © {new Date().getFullYear()} GoWest.ai —{" "}
          {t("assessments.allRightsReserved")} ·{" "}
          <a
            href="mailto:support@bizvantage.ai"
            className="text-accent hover:underline"
          >
            support@bizvantage.ai
          </a>
        </footer>
      </div>

      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("assessments.deleteAssessment")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("assessments.deleteConfirmPrefix")}{" "}
            <span className="font-semibold">
              {deleteDialog.company || t("assessments.thisCompany")}
            </span>
            {t("assessments.deleteConfirmSuffix")}
          </p>
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() =>
                setDeleteDialog((prev) => ({ ...prev, open: false }))
              }
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteDialog.id) {
                  handleDelete(deleteDialog.id);
                }
                setDeleteDialog({ open: false, id: null, company: "" });
              }}
            >
              {t("Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={subscriptionDialogOpen}
        onOpenChange={setSubscriptionDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("dashboard.subscriptionRequired")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>{t("dashboard.subscriptionInfo")}</p>
            <p>{t("assessments.selectNewPlanToContinue")}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { label: "Core", description: "$2,250/qtr" },
                { label: "Pro", description: "$4,500/qtr" },
              ].map((plan) => (
                <button
                  key={plan.label}
                  type="button"
                  onClick={() => setSelectedPlan(plan.label)}
                  className={`rounded-lg border p-4 text-left transition-colors ${
                    selectedPlan === plan.label
                      ? "border-primary bg-primary/5"
                      : "border-border bg-background/80 hover:border-primary"
                  }`}
                >
                  <p className="font-semibold">{plan.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {plan.description}
                  </p>
                </button>
              ))}
            </div>
            {subscriptionError ? (
              <p className="text-sm text-destructive">{subscriptionError}</p>
            ) : null}
          </div>
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setSubscriptionDialogOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={handleStartCheckoutForPlan}
              disabled={subscriptionLoading}
            >
              {subscriptionLoading
                ? t("dashboard.startingCheckout")
                : `${t("common.select")} ${selectedPlan}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AssessmentsNewV1;

function extractLists(data) {
  if (data && Array.isArray(data.active) && Array.isArray(data.completed)) {
    return { activeList: data.active, completedList: data.completed };
  }

  if (Array.isArray(data)) {
    const activeList = data.filter((a) => {
      const s = (a?.status ?? a?.state ?? "").toString().toLowerCase();
      return s === "active" || a?.isActive === true || a?.isCompleted === false;
    });

    const completedList = data.filter((a) => {
      const s = (a?.status ?? a?.state ?? "").toString().toLowerCase();
      return (
        s === "completed" || a?.isCompleted === true || a?.completed === true
      );
    });

    return { activeList, completedList };
  }

  return { activeList: [], completedList: [] };
}

function calcProgress(interviewees) {
  const total = interviewees.length || 0;
  if (total === 0) return 0;
  const withFiles = interviewees.filter((i) => {
    const files = Array.isArray(i.files) ? i.files : [];
    return files.length > 0;
  }).length;
  return Math.round((withFiles / total) * 100);
}
